import { router } from "@inertiajs/vue3";
import type { Ref } from "vue";
import { computed, ref, watch } from "vue";

import {
  archiveOrganizationDiscussionThreadPath,
  organizationDiscussionThreadParticipantGroupPath,
  organizationDiscussionThreadParticipantPath,
  unarchiveOrganizationDiscussionThreadPath,
} from "@/routes";
import type { DiscussionThread, MentionableGroup, MentionableUser } from "@/types";

import { participantDisplayName } from "../formatters";
import type { PendingParticipantRemoval } from "../types";

interface UseDiscussionThreadActionsOptions {
  organizationId: Ref<number | undefined>;
  currentUserId: Ref<number>;
  selectedThread: Ref<DiscussionThread | null>;
}

export function useDiscussionThreadActions({
  organizationId,
  currentUserId,
  selectedThread,
}: UseDiscussionThreadActionsOptions) {
  type ParticipantRemovalKind = PendingParticipantRemoval["kind"];

  const archiveDialogOpen = ref(false);
  const archiveRequestInProgress = ref(false);

  const participantRemovalDialogOpen = ref(false);
  const pendingParticipantRemoval = ref<PendingParticipantRemoval | null>(null);
  const participantRemovalInProgress = ref<number | null>(null);
  const participantGroupRemovalInProgress = ref<number | null>(null);

  const selectedThreadArchivePath = computed(() => {
    if (!organizationId.value || !selectedThread.value) return null;
    return archiveOrganizationDiscussionThreadPath(
      organizationId.value,
      selectedThread.value.id,
    );
  });

  const selectedThreadUnarchivePath = computed(() => {
    if (!organizationId.value || !selectedThread.value) return null;
    return unarchiveOrganizationDiscussionThreadPath(
      organizationId.value,
      selectedThread.value.id,
    );
  });

  const archiveSwitchDisabled = computed(() => {
    const thread = selectedThread.value;
    if (!thread) return true;
    if (archiveRequestInProgress.value) return true;

    return thread.archived ? !thread.can_unarchive : !thread.can_archive;
  });

  const performArchiveChange = (mode: "archive" | "unarchive") => {
    const path =
      mode === "archive"
        ? selectedThreadArchivePath.value
        : selectedThreadUnarchivePath.value;
    if (!path || archiveRequestInProgress.value) return;

    archiveRequestInProgress.value = true;
    router.patch(
      path,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        onSuccess: () => {
          archiveDialogOpen.value = false;
        },
        onFinish: () => {
          archiveRequestInProgress.value = false;
        },
      },
    );
  };

  const confirmArchiveToggle = () => {
    performArchiveChange("archive");
  };

  const handleArchiveSwitchChange = (checked: boolean) => {
    const thread = selectedThread.value;
    if (!thread || archiveRequestInProgress.value) return;

    if (checked) {
      if (!thread.can_archive) return;
      archiveDialogOpen.value = true;
      return;
    }

    if (!thread.can_unarchive) return;
    performArchiveChange("unarchive");
  };

  const participantRemovePath = (
    kind: ParticipantRemovalKind,
    participantId: number,
  ) => {
    if (!organizationId.value || !selectedThread.value) return null;
    if (kind === "group") {
      return organizationDiscussionThreadParticipantGroupPath(
        organizationId.value,
        selectedThread.value.id,
        participantId,
      );
    }
    return organizationDiscussionThreadParticipantPath(
      organizationId.value,
      selectedThread.value.id,
      participantId,
    );
  };

  const inProgressRefForKind = (kind: ParticipantRemovalKind) =>
    kind === "group"
      ? participantGroupRemovalInProgress
      : participantRemovalInProgress;

  const performParticipantRemoval = (
    kind: ParticipantRemovalKind,
    participantId: number,
  ) => {
    const inProgressRef = inProgressRefForKind(kind);
    if (inProgressRef.value) return;

    const path = participantRemovePath(kind, participantId);
    if (!path) return;

    inProgressRef.value = participantId;
    router.delete(path, {
      preserveState: true,
      preserveScroll: true,
      replace: true,
      onFinish: () => {
        inProgressRef.value = null;
      },
    });
  };

  const openParticipantRemovalDialog = (participant: MentionableUser) => {
    if (
      participantRemovalInProgress.value ||
      participantGroupRemovalInProgress.value
    ) {
      return;
    }

    pendingParticipantRemoval.value = {
      kind: "user",
      id: participant.id,
      label: participantDisplayName(participant),
      isCurrentUser: participant.id === currentUserId.value,
    };
    participantRemovalDialogOpen.value = true;
  };

  const openParticipantGroupRemovalDialog = (participant: MentionableGroup) => {
    if (
      participantRemovalInProgress.value ||
      participantGroupRemovalInProgress.value
    ) {
      return;
    }

    pendingParticipantRemoval.value = {
      kind: "group",
      id: participant.id,
      label: `@${participant.name}`,
    };
    participantRemovalDialogOpen.value = true;
  };

  const participantRemovalDialogBusy = computed(() => {
    const pending = pendingParticipantRemoval.value;
    if (!pending) return false;

    return pending.kind === "group"
      ? participantGroupRemovalInProgress.value === pending.id
      : participantRemovalInProgress.value === pending.id;
  });

  const participantRemovalDialogDisabled = computed(
    () =>
      participantRemovalDialogBusy.value ||
      Boolean(participantRemovalInProgress.value) ||
      Boolean(participantGroupRemovalInProgress.value) ||
      !pendingParticipantRemoval.value,
  );

  const confirmParticipantRemoval = () => {
    const pending = pendingParticipantRemoval.value;
    if (!pending) return;

    participantRemovalDialogOpen.value = false;
    performParticipantRemoval(pending.kind, pending.id);
  };

  watch(participantRemovalDialogOpen, (isOpen) => {
    if (!isOpen) {
      pendingParticipantRemoval.value = null;
    }
  });

  return {
    archiveDialogOpen,
    archiveRequestInProgress,
    archiveSwitchDisabled,
    handleArchiveSwitchChange,
    confirmArchiveToggle,
    participantRemovalDialogOpen,
    pendingParticipantRemoval,
    participantRemovalInProgress,
    participantGroupRemovalInProgress,
    participantRemovalDialogDisabled,
    openParticipantRemovalDialog,
    openParticipantGroupRemovalDialog,
    confirmParticipantRemoval,
  };
}
